// Un seul appelant. Il connaît les codes de refus et ne laisse jamais passer une erreur muette.
export class Refus extends Error {
  readonly statut: number;
  readonly code: string;
  /** Les champs que le serveur a refusés, quand il les nomme. */
  readonly champs: string[];
  /** La raison du refus, champ par champ, quand le serveur la donne. */
  readonly raisons: string[];
  constructor(statut: number, code: string, message?: string, champs: string[] = [],
              raisons: string[] = []) {
    super(message ?? code);
    this.statut = statut;
    this.code = code;
    this.champs = champs;
    this.raisons = raisons;
  }
}

/** Les noms de champs tels que le serveur les connaît, dits comme l'écran les affiche. */
const LIBELLES_CHAMPS: Record<string, string> = {
  prenom: 'le prénom', nom: 'le nom', titre: 'le titre', role_grille: 'le rôle',
  types: 'la forme de contrat', tjm_eur: 'le taux journalier',
  salaire_min_eur: 'le salaire minimum', salaire_max_eur: 'le salaire maximum',
  dispo_texte: 'la disponibilité', disponible_a_partir_de: 'la date de disponibilité',
  zones: 'les zones', mobilite: 'la mobilité', expertises: 'les expertises',
  secteurs: 'les secteurs', ia_posture: 'la posture IA', ia_outils: 'les outils IA',
  ia_preuve: 'ce qui prouve la posture', linkedin: 'le lien LinkedIn',
  email: 'le courriel', telephone: 'le téléphone', origine: 'le canal',
  collecte: 'la provenance', intitule: "l'intitulé", enjeu: "l'enjeu",
  client: 'le client', secteur: 'le secteur', region: 'la région',
  budget_min_eur: 'le budget minimum', budget_max_eur: 'le budget maximum',
  demarrage: 'le démarrage', duree_texte: 'la durée', charge_texte: 'la charge',
};

const direChamps = (champs: string[]) =>
  [...new Set(champs.map((c) => LIBELLES_CHAMPS[c] ?? c))].join(', ');

/** Ce que zod renvoie quand il refuse une valeur. On n'en lit que ce qui aide à corriger. */
interface Issue {
  code?: string; path?: unknown[]; maximum?: number; minimum?: number; type?: string;
}

/**
 * La raison du refus, en français.
 *
 * Le serveur renvoie le détail de zod, en anglais et dans son vocabulaire. « À corriger :
 * les expertises » dit où ; il manque quoi. Une liste de trente et une expertises et une
 * expertise de quatre-vingt-dix caractères se corrigent de deux façons opposées, et sans
 * cette phrase il n'y a qu'un moyen de savoir laquelle : essayer.
 */
function direIssue(d: Issue): string {
  const champ = LIBELLES_CHAMPS[String(d.path?.[0] ?? '')] ?? String(d.path?.[0] ?? 'ce champ');
  if (d.code === 'too_big' && typeof d.maximum === 'number')
    return d.type === 'array'
      ? `${champ} : ${d.maximum} valeurs au maximum`
      : `${champ} : ${d.maximum} caractères au maximum`;
  if (d.code === 'too_small' && typeof d.minimum === 'number')
    return d.type === 'array'
      ? `${champ} : ${d.minimum} valeur${d.minimum > 1 ? 's' : ''} au minimum`
      : `${champ} : ${d.minimum} caractère${d.minimum > 1 ? 's' : ''} au minimum`;
  return '';
}

async function appeler<T>(methode: string, chemin: string, corps?: unknown): Promise<T> {
  const init: RequestInit = { method: methode, credentials: 'same-origin' };
  if (corps instanceof FormData) init.body = corps;
  else if (corps !== undefined) {
    init.headers = { 'content-type': 'application/json' };
    init.body = JSON.stringify(corps);
  }
  const reponse = await fetch(chemin, init);

  if (reponse.status === 204) return undefined as T;
  const charge = reponse.headers.get('content-type')?.includes('json') ? await reponse.json() : null;

  // Un 401 se propage à l'appelant, qui décide quoi faire. Recharger la page ici
  // provoquerait une boucle : la première requête d'un visiteur non connecté est
  // justement celle qui répond 401.
  if (!reponse.ok) {
    // Le serveur renvoie le détail zod ; s'en priver à l'affichage, c'est laisser
    // l'utilisateur deviner lequel de vingt champs il doit corriger.
    const issues = Array.isArray(charge?.details) ? charge.details as Issue[] : [];
    const champs = issues.map((d) => String(d.path?.[0] ?? '')).filter(Boolean);
    throw new Refus(reponse.status, charge?.erreur ?? 'erreur', charge?.message, champs,
                    issues.map(direIssue).filter(Boolean));
  }
  return charge as T;
}

/**
 * Un fichier produit à la demande : le CV WAKEA, le dossier de présentation.
 *
 * Pas de `location.href` ici, contrairement au téléchargement d'un document déposé. La
 * génération prend plusieurs secondes et peut être refusée — un dossier trop maigre, un
 * moteur injoignable — et une navigation afficherait le JSON du refus en pleine page au
 * lieu de le dire à l'écran. On passe donc par `fetch`, et l'appelant garde la main.
 */
export async function fichier(chemin: string): Promise<string> {
  const reponse = await fetch(chemin, { method: 'POST', credentials: 'same-origin' });
  if (!reponse.ok) {
    const charge = reponse.headers.get('content-type')?.includes('json')
      ? await reponse.json().catch(() => null) : null;
    throw new Refus(reponse.status, charge?.erreur ?? 'erreur', charge?.message, []);
  }
  const entete = reponse.headers.get('content-disposition') ?? '';
  const m = /filename\*=UTF-8''([^;]+)/.exec(entete);
  const nom = m?.[1] ? decodeURIComponent(m[1]) : 'document';

  const url = URL.createObjectURL(await reponse.blob());
  const lien = document.createElement('a');
  lien.href = url; lien.download = nom;
  document.body.appendChild(lien); lien.click(); lien.remove();
  // Révoquer tout de suite couperait le téléchargement dans certains navigateurs ; le
  // laisser vivre fuirait la mémoire. Une minute suffit largement à l'enregistrement.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return nom;
}

export const api = {
  get: <T>(chemin: string) => appeler<T>('GET', chemin),
  post: <T>(chemin: string, corps?: unknown) => appeler<T>('POST', chemin, corps),
  patch: <T>(chemin: string, corps?: unknown) => appeler<T>('PATCH', chemin, corps),
  remplacer: <T>(chemin: string, corps?: unknown) => appeler<T>('PUT', chemin, corps),
  supprimer: <T>(chemin: string, corps?: unknown) => appeler<T>('DELETE', chemin, corps),
};

export const parametres = (o: Record<string, string | number | boolean | string[] | undefined>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) {
    // Une liste part en valeurs séparées par des virgules ; vide, elle ne part pas du tout.
    // `String([])` vaut la chaîne vide, et un `chaine=` sans valeur obligerait le serveur à
    // apprendre à l'ignorer — une règle de moins s'il ne la reçoit jamais.
    if (Array.isArray(v)) { if (v.length) p.set(k, v.join(',')); continue; }
    if (v !== undefined && v !== '' && v !== false) p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
};

/** Messages de refus, en français, au même endroit — l'interface n'invente rien. */
export const REFUS: Record<string, string> = {
  requete_invalide: 'Une valeur saisie est refusée.',
  non_authentifie: 'Votre session a expiré. Reconnectez-vous.',
  identifiants_refuses: 'Adresse ou mot de passe refusés.',
  code_refuse: "Ce code n'est pas valable. Vérifiez l'heure de votre téléphone.",
  etape_expiree: 'La connexion a expiré. Reprenez depuis le début.',
  trop_de_tentatives: 'Trop de tentatives. Patientez quelques minutes.',
  domaine_refuse: 'Seules les adresses du cabinet sont acceptées.',
  lien_invalide: 'Ce lien a déjà servi ou a expiré. Demandez-en un nouveau.',
  mot_de_passe_faible: 'Mot de passe trop simple.',
  entretien_manquant: "Joignez d'abord un compte rendu d'entretien.",
  droits_insuffisants: "Votre rôle ne permet pas ce geste.",
  compte_existant: 'Un compte existe déjà pour cette adresse.',
  dernier_administrateur: 'Il doit rester au moins un administrateur actif.',
  type_refuse: "Format refusé pour cette catégorie. Un CV et un compte rendu d'entretien doivent être lisibles : PDF, Word .docx, .txt ou .md. Les autres pièces acceptent aussi .doc, .png et .jpg.",
  contenu_non_conforme: "Le contenu du fichier ne correspond pas à son extension.",
  fichier_trop_lourd: 'Fichier trop lourd — quinze mégaoctets au maximum.',
  document_vide: "Ce document ne contient aucun texte lisible. S'il s'agit d'un scan, reprenez-le en PDF texte.",
  ia_indisponible: "La lecture par le modèle n'est pas configurée sur cet environnement.",
  ia_injoignable: "Le modèle n'a pas répondu. Réessayez dans un instant.",
  extraction_illisible: "Le modèle a répondu dans une forme que MANA n'a pas su lire. Réessayez ; si cela se reproduit, saisissez la fiche à la main.",
  document_sans_texte: "Ce document ne contient aucun texte lisible — c'est sans doute un CV scanné ou une image. Demandez un PDF texte, ou saisissez la fiche à la main.",
  format_illisible: "Ce CV est dans un format que le modèle ne sait pas ouvrir — un Word d'avant 2007, par exemple. Il reste au dossier ; complétez la fiche à la main.",
  cv_format: "Ce CV n'est pas dans un format lisible. Enregistrez-le en PDF ou en Word récent (.docx).",
  document_illisible: "Le document n'a pas pu être relu dans le stockage. Réessayez dans un instant.",
  budget_ia_epuise: "Le plafond de dépense IA du mois est atteint. Un administrateur peut le relever.",
  traitement_inconnu: 'Cette lecture a expiré ou a déjà servi. Reprenez depuis le dépôt du CV.',
  fiche_illisible: 'La fiche envoyée est mal formée. Reprenez depuis le dépôt du CV.',
  notice_echec: "La notice n'a pas pu partir. L'échec est inscrit au journal ; vérifiez l'adresse.",
  lien_expire: 'Ce lien a expiré.',
  synthese_illisible: "Le modèle a répondu dans une forme que MANA n'a pas su lire. Réessayez dans un instant.",
  dossier_vide: "Il n'y a encore rien à synthétiser : ni expérience, ni entretien, ni note.",
  normalisation_illisible: "Le modèle a répondu dans une forme que MANA n'a pas su lire. Réessayez, ou remplissez la fiche à la main.",
  besoin_trop_court: 'Collez la demande du client — au moins quelques lignes.',
  motif_obligatoire: 'Le motif est obligatoire : il dit où le rapprochement s’est trompé.',
  deja_rattache: 'Ce profil est déjà rattaché à la mission.',
  rien_a_modifier: 'Rien à enregistrer.',
  erreur_interne: "Une erreur est survenue. Elle est inscrite au journal.",
};

export const direRefus = (e: unknown) => {
  if (!(e instanceof Refus)) return 'Le serveur est injoignable.';
  const base = REFUS[e.code] ?? e.message ?? 'Geste refusé.';
  // La raison l'emporte sur la liste des champs : elle les nomme déjà, et elle dit en plus
  // ce qui cloche. Nommer un champ sans dire pourquoi oblige à corriger au hasard.
  if (e.raisons.length) return `${base} ${[...new Set(e.raisons)].join(' · ')}.`;
  return e.champs.length ? `${base} À corriger : ${direChamps(e.champs)}.` : base;
};
